/**
 * SA-1295: what an email triage row shows of its email, and what the chip on
 * that email says about its row. A row's `providerMessageId` names its email's
 * body message (`channelMetadata.provider_message_id`); an attachment row
 * names its body as `email_provider_message_id` and is never a row's email.
 * Pure.
 */
import type { InboxMessage, TriageRow } from '@/types/inbox'
import { stripLeadingBanners, stripTrailingBanners } from './emailBanners'
import { splitQuoted, type QuoteSplit } from './emailQuote'
import { whenText } from '@/components/process/stepLiveText'
import { attachmentsByBody } from './emailThread'
import { excerpt } from './groupMessages'
import { attachmentMeta } from './mediaGallery'
import { chipFor, filesPart, NO_OWN_TEXT, QUOTED_TEXT } from './triageText'

export interface EmailRowMessage {
  messageId: string
  sender: string | null
  when: string
  /** The email's subject, only when it differs from the thread's. */
  subject: string | null
  text: string
  /** SA-1308: a waiting row's text, the first lines of the email's own words;
   * empty when it has none. */
  lines: string[]
  /** SA-1329: non-blank lines of its own words beyond the lines shown. */
  moreLines: number
  /** SA-1329: its files, when the email has text of its own as well (the
   * text line names the files of an email without); else 0. */
  fileCount: number
}

/** SA-1308: how many lines of its email a waiting row shows. */
export const WAITING_LINES = 2

/** The first non-blank lines of an email's own words, each trimmed. */
export function waitingLines(visible: string): string[] {
  return nonBlankLines(visible).slice(0, WAITING_LINES)
}

const nonBlankLines = (visible: string): string[] => visible.split('\n').map((line) => line.trim()).filter(Boolean)

export interface EmailRowChip { ledgerId: string; text: string; waiting: boolean }

const str = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null)

const SUBJECT_LINE = /^Subject: [^\n]*\n?\n?/

/** SA-1308: the ONE reader of an email's own words. A leading stored
 * `Subject:` line is dropped, whatever its subject (line 1 names a differing
 * one), then the banners opening the words, then the quoted block, then the
 * banners closing what is left. The banners go before the split because a
 * Mimecast notice's closing rule would otherwise join a quoted block below it. */
export function ownWords(content: string): QuoteSplit {
  const split = splitQuoted(stripLeadingBanners(content.replace(SUBJECT_LINE, '')))
  return { visible: stripTrailingBanners(split.visible), quoted: split.quoted }
}

/** The email's own words, without the quoted block. */
export function ownText(content: string): string {
  return ownWords(content).visible
}

function textLine(split: QuoteSplit, names: readonly string[]): string {
  const own = excerpt(split.visible, 160)
  if (own) return own
  return [NO_OWN_TEXT, split.quoted ? QUOTED_TEXT : null, names.length ? filesPart(names) : null]
    .filter((part): part is string => !!part)
    .join(' · ')
}

/** Each email row's email, by ledger id; a row whose email the thread did
 * not load is left out. `zone` and `now` are for tests. */
export function emailRowMessages(
  rows: ReadonlyArray<Pick<TriageRow, 'ledgerId' | 'providerMessageId'>>,
  messages: readonly InboxMessage[],
  threadSubject: string | null,
  zone?: string,
  now: Date = new Date(),
): Record<string, EmailRowMessage> {
  const byId = new Map<string, InboxMessage>()
  for (const m of messages) {
    const id = m.channel === 'email' ? str(m.channelMetadata?.provider_message_id) : null
    if (id && !byId.has(id)) byId.set(id, m)
  }
  const { attachments } = attachmentsByBody(messages)
  const out: Record<string, EmailRowMessage> = {}
  for (const row of rows) {
    const body = row.providerMessageId ? byId.get(row.providerMessageId) : undefined
    if (!body) continue
    const meta = body.channelMetadata ?? {}
    const subject = str(meta.subject)
    const own = ownWords(body.content ?? '')
    const names = (attachments.get(body.id) ?? []).map((f) => attachmentMeta(f).filename).filter((n): n is string => !!n)
    // One rule for "text of its own": the text line's own excerpt.
    const hasOwn = !!excerpt(own.visible, 160)
    out[row.ledgerId] = {
      messageId: body.id,
      sender: str(meta.from_name) ?? str(meta.from),
      when: Number.isFinite(body.timestamp) ? whenText(new Date(body.timestamp).toISOString(), now, zone) ?? '' : '',
      subject: subject && (threadSubject ?? '').trim() !== subject.trim() ? subject : null,
      text: textLine(own, names),
      lines: hasOwn ? waitingLines(own.visible) : [],
      moreLines: hasOwn ? Math.max(0, nonBlankLines(own.visible).length - WAITING_LINES) : 0,
      fileCount: hasOwn ? names.length : 0,
    }
  }
  return out
}

/** The chip each triaged email carries, by message id; the first row in
 * served order names an email two rows share. */
export function emailChips(
  rows: readonly TriageRow[], rowMessages: Record<string, EmailRowMessage>,
): Map<string, EmailRowChip> {
  const out = new Map<string, EmailRowChip>()
  for (const row of rows) {
    const m = rowMessages[row.ledgerId]
    if (!m || out.has(m.messageId)) continue
    out.set(m.messageId, { ledgerId: row.ledgerId, ...chipFor(row) })
  }
  return out
}
